// ============================================================================
// P-19a (D-145 / D-146, Q-99) — what can be attached / recorded on this
// device, as PURE logic (no React, no DOM access). Shared by the ticket side
// (MediaCapture in "שליחת עדכון ללקוח") and the portal (P-19b; its
// components/portal/portalMediaModel.ts re-exports the capability helpers from
// here and keeps only the portal's limits — unified at MERGE-INT7).
//
//   desktop: file / screenshot · voice recording · screen recording
//   phone:   file · voice recording   (no screen recording — Q-99)
// Unit-tested in src/utils/__tests__/mediaModel.test.ts.
// ============================================================================

export interface MediaEnv {
  userAgent: string;
  /** navigator.maxTouchPoints (iPadOS reports a desktop UA but has touch points). */
  maxTouchPoints: number;
  /** navigator.mediaDevices.getDisplayMedia exists. */
  hasGetDisplayMedia: boolean;
  /** window.MediaRecorder exists. */
  hasMediaRecorder: boolean;
  /** navigator.mediaDevices.getUserMedia exists (microphone). */
  hasGetUserMedia?: boolean;
}

export interface MediaCapabilities {
  file: boolean;
  voice: boolean;
  screen: boolean;
  /** Phone or tablet: the reduced set (file + voice). */
  mobile: boolean;
}

const MOBILE_UA = /Android|iPhone|iPad|iPod|Mobile|Windows Phone|IEMobile|Opera Mini|webOS|BlackBerry/i;

/** A phone / tablet (by UA, or a Mac-looking UA with a touch screen = iPadOS). */
export function isMobileDevice(userAgent: string, maxTouchPoints: number): boolean {
  if (MOBILE_UA.test(userAgent || '')) return true;
  return /Macintosh/i.test(userAgent || '') && maxTouchPoints > 1;
}

export function mediaCapabilities(env: MediaEnv): MediaCapabilities {
  const mobile = isMobileDevice(env.userAgent, env.maxTouchPoints);
  const mic = env.hasGetUserMedia !== false;
  return {
    file: true,
    voice: env.hasMediaRecorder && mic,
    screen: !mobile && env.hasMediaRecorder && env.hasGetDisplayMedia,
    mobile,
  };
}

/** The browser's environment (the only impure helper; not unit-tested). */
export function browserMediaEnv(): MediaEnv {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const md = nav?.mediaDevices as (MediaDevices & { getDisplayMedia?: unknown }) | undefined;
  return {
    userAgent: nav?.userAgent ?? '',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    hasGetDisplayMedia: typeof md?.getDisplayMedia === 'function',
    hasMediaRecorder: typeof window !== 'undefined' && typeof (window as { MediaRecorder?: unknown }).MediaRecorder === 'function',
    hasGetUserMedia: typeof md?.getUserMedia === 'function',
  };
}

/** Recordings stop by themselves after this long. */
export const VOICE_MAX_MS = 5 * 60 * 1000;
export const SCREEN_MAX_MS = 5 * 60 * 1000;
/** Default file limit (the ticket attachment route: 10 MB). */
export const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

/** A screen recording's file name (Hebrew-safe, sortable). */
export function screenRecordingName(now: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `screen-${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}.webm`;
}

/** Split picked files into accepted ones (size, room left) and rejected names. */
export function acceptFiles<T extends { size: number; name: string }>(files: T[], remaining: number, maxBytes = DEFAULT_MAX_BYTES): { accepted: T[]; tooLarge: string[]; overLimit: string[] } {
  const tooLarge: string[] = [];
  const fit = files.filter((f) => (f.size <= maxBytes ? true : (tooLarge.push(f.name), false)));
  const room = Math.max(0, remaining);
  return { accepted: fit.slice(0, room), tooLarge, overLimit: fit.slice(room).map((f) => f.name) };
}

/** The first MediaRecorder video type the browser supports (screen recording). */
export function pickScreenMime(isTypeSupported: (t: string) => boolean): string {
  for (const t of ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4']) {
    try {
      if (isTypeSupported(t)) return t;
    } catch {
      /* keep looking */
    }
  }
  return '';
}

/** File name of a recording: "<label>-YYYY-MM-DD-HHMM.<ext>". */
export function recordingFileName(label: string, mime: string, now: Date): string {
  const ext = /mp4/i.test(mime) ? 'mp4' : 'webm';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${label}-${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}.${ext}`;
}

/** m:ss of a running recording. */
export function recordingClock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ---- P-59a (D-235 §1 + §4) ------------------------------------------------

/** The screenshot button: desktop with getDisplayMedia (no MediaRecorder needed - one frame). */
export function screenshotAvailable(env: MediaEnv): boolean {
  return !isMobileDevice(env.userAgent, env.maxTouchPoints) && env.hasGetDisplayMedia;
}

/** A screenshot's file name (Hebrew-safe, sortable). */
export function screenshotName(now: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `screenshot-${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}.png`;
}

/**
 * FIX-B38 (QX-800): a voice message recorded in the app. Its file is a WebM the
 * upload validator may label video/webm (the same EBML container as a screen
 * recording), so the name decides: VoiceRecorder names it `recording_<ms>.<ext>`
 * (also accepted: `voice-*` / `voice_*`), or the browser's audio-only
 * `video/webm;codecs=opus` type. A screen recording (`screen-*`) is never voice.
 */
export function isVoiceRecording(mime: string | null | undefined, name = ''): boolean {
  const base = (name || '').split(/[\\/]/).pop() || '';
  if (/^(recording_\d+|voice[-_][^.]*)\.(webm|ogg|oga|m4a|mp4|wav|mp3|weba)$/i.test(base)) return true;
  return (mime || '').toLowerCase().replace(/\s+/g, '') === 'video/webm;codecs=opus';
}

const AUDIO_EXT_MIME: Record<string, string> = {
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', oga: 'audio/ogg', opus: 'audio/ogg',
  m4a: 'audio/mp4', aac: 'audio/aac', weba: 'audio/webm',
};
const AUDIO_EXT_RE = /\.(mp3|wav|ogg|oga|opus|m4a|aac|weba)$/i;

/**
 * FIX-AUDIO: the type to give an <audio> blob. The server label (blob type) or the stored mime may be
 * video/webm (a voice note), application/octet-stream or empty - the player then refuses it. Pick an audio/* type:
 * the blob's/stored one when it already is audio, else by the file extension, else audio/webm (the recorder default).
 */
export function audioBlobType(blobType: string | null | undefined, storedMime: string | null | undefined, name = ''): string {
  for (const t of [blobType, storedMime]) {
    const m = (t || '').toLowerCase().split(';')[0].trim();
    if (m.startsWith('audio/')) return m;
  }
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (AUDIO_EXT_MIME[ext]) return AUDIO_EXT_MIME[ext];
  const v = (blobType || storedMime || '').toLowerCase().split(';')[0].trim();
  if (v.startsWith('video/')) return 'audio/' + (v.split('/')[1] || 'webm');
  return 'audio/webm';
}

export type AttachmentKind = 'image' | 'video' | 'audio' | 'file';

/** How an attachment is shown in a conversation: inline image / video player / audio player / download chip. */
export function attachmentKind(mime: string | null | undefined, name = ''): AttachmentKind {
  const m = (mime || '').toLowerCase().split(';')[0].trim();
  if (['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(m)) return 'image';
  // FIX-B38 (QX-800): a voice recording stays audio even when stored as video/webm.
  if (isVoiceRecording(mime, name)) return 'audio';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (!m || m === 'application/octet-stream') {
    if (/\.(png|jpe?g|gif|webp)$/i.test(name)) return 'image';
    if (/\.(mp4|webm|mov)$/i.test(name)) return 'video';
    // FIX-AUDIO: a voice file whose type was lost (octet-stream) still plays by its extension.
    if (AUDIO_EXT_RE.test(name)) return 'audio';
  }
  return 'file';
}

// ---- FIX-B43 (FQ-060, D-235 §1) -------------------------------------------

/** How long after the ticket's creation a ticket-level file still counts as "attached when opening the request". */
export const OPENING_WINDOW_MS = 10 * 60 * 1000;

interface OpeningFileLike {
  id: string;
  comment_id?: string | null;
  created_at: string;
}

/**
 * The files attached when the request was opened: ticket-level (no `comment_id`) and
 * created with the ticket (within OPENING_WINDOW_MS of it). They show inside the first
 * bubble of the conversation; a file uploaded later to the ticket (the files tab) stays
 * a tab-only row. Files already carried by a message bubble (`shownIds`) are never repeated.
 */
export function openingAttachments<T extends OpeningFileLike>(
  ticketAttachments: T[],
  ticketCreatedAt: string | null | undefined,
  shownIds: Iterable<string> = [],
): T[] {
  const created = ticketCreatedAt ? new Date(ticketCreatedAt).getTime() : NaN;
  if (Number.isNaN(created)) return [];
  const shown = new Set(shownIds);
  return ticketAttachments
    .filter((a) => {
      if (a.comment_id || shown.has(a.id)) return false;
      const at = new Date(a.created_at).getTime();
      return !Number.isNaN(at) && at >= created - 1000 && at - created <= OPENING_WINDOW_MS;
    })
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}
